// src/axiosInstance.js
import axios from 'axios';

// Normaliza ORIGIN y baseURL evitando /api duplicado
const RAW_ORIGIN = import.meta.env.VITE_API_URL || window.location.origin || '';
const ORIGIN_CLEAN = RAW_ORIGIN.replace(/\/+$/, '');
const baseURL = ORIGIN_CLEAN.endsWith('/api') ? ORIGIN_CLEAN : `${ORIGIN_CLEAN}/api`;

// Crea instancia
const axiosInstance = axios.create({
  baseURL,
  timeout: 15000, // 15s
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Accept-Language': navigator.language || 'es',
  },
});

axiosInstance.defaults.xsrfCookieName = 'csrftoken';
axiosInstance.defaults.xsrfHeaderName = 'X-CSRFToken';

// Estado global de clínica activa
let currentClinicId = null;

export function setAxiosClinicId(id) {
  currentClinicId = id ? Number(id) : null;
}

// Helpers
const getPathname = (url = '') => {
  try {
    const u = new URL(url, baseURL);
    const p = u.pathname || '/';
    return p.startsWith('/api/') ? p.slice(4) : (p === '/api' ? '/' : p);
  } catch {
    return url.split('?')[0] || '/';
  }
};

const isAuthEndpoint = (url = '') => {
  const path = getPathname(url);
  return [
    '/auth/login',
    '/auth/registration',
    '/auth/password/reset',
    '/auth/password/reset/confirm',
    '/auth/google',
    '/auth/get-token-from-session',
    '/patients/login',
    '/patients/register',
    '/patients/reset-password-request',
    '/patients/reset-password-confirm',
  ].some((p) => path.startsWith(p));
};

const isPatientApi = (url = '') => {
  const path = getPathname(url);
  return path.startsWith('/patients/');
};

// Endpoints clave del flujo de paciente (booking/pago)
const isPatientFlowEndpoint = (url = '') => {
  const path = getPathname(url);
  return [
    '/appointments/create-payment-order',
    '/appointments/confirm',
    '/appointments/guest-booking',
    '/paypal/create-order',
    '/paypal/capture-order',
  ].some((p) => path.startsWith(p));
};

// Detectar si estamos en el portal del paciente
const inPatientPortal = (() => {
  try {
    return (window.location.pathname || '').startsWith('/patient');
  } catch {
    return false;
  }
})();

// Interceptor de request
axiosInstance.interceptors.request.use(
  (config) => {
    const path = getPathname(config.url || '');
    config.headers = config.headers || {};

    // 1) Autenticación/Invitado
    // Limpia token de invitado por defecto
    delete config.headers['X-Guest-Token'];

    const isGuest = localStorage.getItem('isGuest') === 'true';
    const guestToken = localStorage.getItem('guestToken');

    if (isGuest && guestToken) {
      // Invitado: no se envía Authorization
      delete config.headers.Authorization;
      config.headers['X-Guest-Token'] = guestToken;
    } else {
      // Tokens de usuario
      const patientToken = localStorage.getItem('patientToken');

      // IMPORTANTE: usa authToken como token del médico (fallback de doctorToken)
      const doctorToken =
        localStorage.getItem('doctorToken') || localStorage.getItem('authToken');

      // Reglas:
      // - API pacientes / portal pacientes / flujo booking => patientToken
      // - Resto => doctorToken
      if (
        isPatientApi(path) ||
        isPatientFlowEndpoint(path) ||
        inPatientPortal
      ) {
        if (patientToken) {
          config.headers.Authorization = `Token ${patientToken}`;
        } else {
          delete config.headers.Authorization;
        }
      } else {
        if (doctorToken) {
          config.headers.Authorization = `Token ${doctorToken}`;
        } else {
          delete config.headers.Authorization;
        }
      }

      // Asegurar token en /fda-search si hay alguno
      if (path.startsWith('/fda-search')) {
        if (doctorToken) {
          config.headers.Authorization = `Token ${doctorToken}`;
        } else if (patientToken) {
          config.headers.Authorization = `Token ${patientToken}`;
        }
      }
    }

    // 2) X-Clinic-Id (control centralizado)
    // Limpia SIEMPRE primero para evitar residuos
    delete config.headers['X-Clinic-Id'];

    // Permite saltarse el header para llamadas puntuales
    const skipClinic = config?.meta?.skipClinicHeader === true;

    // Solo agrega si hay clínica activa y no se pidió saltarlo
    if (!skipClinic && currentClinicId) {
      config.headers['X-Clinic-Id'] = String(currentClinicId);
    }

    // 3) Endpoints de autenticación: por seguridad, no enviar X-Clinic-Id ni X-Guest-Token
    if (isAuthEndpoint(path)) {
      delete config.headers['X-Clinic-Id'];
      delete config.headers['X-Guest-Token'];
      // Authorization puede ir según el caso (p.ej. exchange token por sesión),
      // pero si quieres forzar que no haya token, descomenta:
      // delete config.headers.Authorization;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de response
axiosInstance.interceptors.response.use(
  (res) => res,
  (err) => {
    const st = err?.response?.status;
    if (st === 429) console.warn('HTTP 429 (rate limited):', err.config?.url);
    return Promise.reject(err);
  }
);

export default axiosInstance;
